import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {SiteModel} from "../../../../../models/site.model";
import {SitesService} from "../../../../shared/services/sites.service";
import {TranslateService} from "@ngx-translate/core";
import {NotificationService} from "../../../../shared/services/notification.service";

interface DialogData {
    site?: SiteModel;
}

@Component({
    selector: 'app-site-modal',
    templateUrl: './site-modal.component.html',
    styleUrls: ['./site-modal.component.scss']
})
export class SiteModalComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    siteForm!: FormGroup;
    isSubmitting = false;
    isEditMode: boolean = false;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<SiteModalComponent>,
        @Inject(MAT_DIALOG_DATA) private data: DialogData,
        private sitesService: SitesService,
        private translate: TranslateService,
        private notificationService: NotificationService,
    ) {
        this.isEditMode = !!this.data?.site;
    }

    ngOnInit(): void {
        this.initForm();

        if (this.isEditMode && this.data.site) {
            this.siteForm.patchValue({
                label: this.data.site.label
            });
        }
    }

    private initForm(): void {
        this.siteForm = this.fb.group({
            label: ['', Validators.required]
        });
    }

    onSubmit(): void {
        if (this.siteForm.valid && !this.isSubmitting) {
            this.isSubmitting = true;
            const formValue = this.siteForm.value;
            const siteData = {
                label: formValue.label
            };

            const request = this.isEditMode
                ? this.sitesService.update(this.data.site!.id, siteData)
                : this.sitesService.create(siteData);

            request.pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isSubmitting = false)
            ).subscribe({
                next: (response) => {
                    const messageKey = this.isEditMode
                        ? 'pages.sites_management.site_updated_successfully'
                        : 'pages.sites_management.site_created_successfully';
                    this.notificationService.success(
                        this.translate.instant(messageKey),
                        this.translate.instant('general.actions.close'),
                        {duration: 3000}
                    );
                    this.dialogRef.close(response);
                },
                error: () => {
                    const messageKey = this.isEditMode
                        ? 'pages.sites_management.site_update_failed'
                        : 'pages.sites_management.site_creation_failed';
                    this.notificationService.error(
                        this.translate.instant(messageKey),
                        this.translate.instant('general.actions.close'),
                        {duration: 3000}
                    );
                }
            });
        }
    }

    closeModal(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
